import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Fuel, 
  DollarSign, 
  Droplets, 
  AlertTriangle, 
  Check, 
  X, 
  Clock,
  Gauge,
  Car,
  CreditCard,
  Banknote,
  RotateCcw,
  Volume2,
  VolumeX,
  Settings,
  TrendingUp,
  Database,
  Store,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  Search,
  Coffee,
  Cigarette,
  Wine,
  Cookie,
  Sandwich,
  Package,
  Receipt,
  Printer,
  Play,
  Pause,
  SkipForward,
  RefreshCw,
  Monitor,
  Zap,
  Users,
  Award,
  Shield,
  ChevronRight,
  Sparkles,
  LogIn
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// Pump Status Types
const PUMP_STATUS = {
  IDLE: 'idle',
  AUTHORIZED: 'authorized',
  PUMPING: 'pumping',
  COMPLETE: 'complete',
  OFFLINE: 'offline',
};

// Fuel Grades
const FUEL_GRADES = [
  { id: 'regular', name: 'Regular', octane: '87', color: 'bg-green-500', price: 3.299 },
  { id: 'plus', name: 'Plus', octane: '89', color: 'bg-blue-500', price: 3.599 },
  { id: 'premium', name: 'Premium', octane: '93', color: 'bg-purple-500', price: 3.999 },
  { id: 'diesel', name: 'Diesel', octane: 'D2', color: 'bg-yellow-500', price: 3.799 },
];

// C-Store Categories
const STORE_CATEGORIES = [
  { id: 'drinks', name: 'Drinks', icon: Coffee },
  { id: 'snacks', name: 'Snacks', icon: Cookie },
  { id: 'tobacco', name: 'Tobacco', icon: Cigarette },
  { id: 'food', name: 'Food', icon: Sandwich },
];

// Sample Products
const STORE_PRODUCTS = [
  { id: 1, name: 'Coffee Large', price: 2.49, category: 'drinks', icon: '☕' },
  { id: 2, name: 'Red Bull', price: 3.99, category: 'drinks', icon: '🥤' },
  { id: 3, name: 'Monster Energy', price: 3.49, category: 'drinks', icon: '🥤' },
  { id: 4, name: 'Coca-Cola 20oz', price: 2.29, category: 'drinks', icon: '🥤' },
  { id: 5, name: 'Water Bottle', price: 1.99, category: 'drinks', icon: '💧' },
  { id: 6, name: 'Doritos', price: 2.99, category: 'snacks', icon: '🔺' },
  { id: 7, name: 'Lay\'s Chips', price: 2.49, category: 'snacks', icon: '🥔' },
  { id: 8, name: 'Snickers', price: 1.89, category: 'snacks', icon: '🍫' },
  { id: 9, name: 'Beef Jerky', price: 6.99, category: 'snacks', icon: '🥩' },
  { id: 10, name: 'Marlboro Red', price: 9.99, category: 'tobacco', icon: '🚬', ageRestricted: true },
  { id: 11, name: 'Juul Pods', price: 15.99, category: 'tobacco', icon: '💨', ageRestricted: true },
  { id: 12, name: 'Hot Dog', price: 2.49, category: 'food', icon: '🌭' },
  { id: 13, name: 'Pizza Slice', price: 3.49, category: 'food', icon: '🍕' },
  { id: 14, name: 'Nachos', price: 4.49, category: 'food', icon: '🧀' },
];

// Demo scenarios for auto-play
const DEMO_SCENARIOS = [
  { type: 'prepay', pump: 1, amount: 25, grade: 'regular', delay: 2000 },
  { type: 'prepay', pump: 4, amount: 40, grade: 'premium', delay: 4000 },
  { type: 'store_sale', items: [1, 6, 8], delay: 6000 },
  { type: 'prepay', pump: 2, amount: 30, grade: 'diesel', delay: 8000 },
  { type: 'store_sale', items: [2, 7, 12], delay: 10000 },
  { type: 'prepay', pump: 6, amount: 50, grade: 'regular', delay: 12000 },
];

// Customer names for demo
const CUSTOMER_NAMES = ['John D.', 'Sarah M.', 'Mike T.', 'Lisa K.', 'David R.', 'Emma S.'];

// Initial pumps state
const createInitialPumps = () => Array.from({ length: 8 }, (_, i) => ({
  id: i + 1,
  status: PUMP_STATUS.IDLE,
  currentAmount: 0,
  currentGallons: 0,
  presetAmount: null,
  selectedGrade: null,
  customerName: null,
}));

const GasStationDemo = () => {
  const navigate = useNavigate();
  
  // Demo controls
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoSpeed, setDemoSpeed] = useState(1); // 1x, 2x, 3x
  const [showDemoPanel, setShowDemoPanel] = useState(true);
  
  // Stats
  const [todayStats, setTodayStats] = useState({
    totalSales: 2847.50,
    gallonsSold: 862,
    transactions: 47,
    storesSales: 534.20,
    fuelSales: 2313.30,
  });
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  
  // Pump state
  const [pumps, setPumps] = useState(createInitialPumps());
  const [selectedPump, setSelectedPump] = useState(null);
  const [showPrepayDialog, setShowPrepayDialog] = useState(false);
  
  // Store state
  const [selectedCategory, setSelectedCategory] = useState('drinks');
  const [cart, setCart] = useState([]);
  const [showAgeVerify, setShowAgeVerify] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  
  // Tank levels
  const [tankLevels, setTankLevels] = useState({
    regular: 71,
    plus: 53,
    premium: 60,
    diesel: 18,
  });

  // Add notification
  const addNotification = useCallback((message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);

  // Simulate pumping
  useEffect(() => {
    const interval = setInterval(() => {
      setPumps(prev => prev.map(pump => {
        if (pump.status === PUMP_STATUS.PUMPING && pump.presetAmount) {
          const increment = 0.35 * demoSpeed;
          const newAmount = Math.min(pump.currentAmount + increment, pump.presetAmount);
          const gradePrice = FUEL_GRADES.find(g => g.id === pump.selectedGrade)?.price || 3.299;
          const newGallons = newAmount / gradePrice;
          
          if (newAmount >= pump.presetAmount) {
            addNotification(`Pump ${pump.id} complete - $${pump.presetAmount.toFixed(2)}`, 'success');
            return { 
              ...pump, 
              currentAmount: pump.presetAmount, 
              currentGallons: pump.presetAmount / gradePrice, 
              status: PUMP_STATUS.COMPLETE 
            };
          }
          return { ...pump, currentAmount: newAmount, currentGallons: newGallons };
        }
        return pump;
      }));
    }, 300);
    return () => clearInterval(interval);
  }, [demoSpeed, addNotification]);

  // Demo cycle counter to trigger new scenarios
  const [demoCycle, setDemoCycle] = useState(0);
  
  // Auto demo runner - only depends on demoRunning, demoSpeed, and demoCycle
  useEffect(() => {
    if (!demoRunning) return;
    
    const timeouts = DEMO_SCENARIOS.map((scenario, index) => {
      return setTimeout(() => {
        if (scenario.type === 'prepay') {
          const customerName = CUSTOMER_NAMES[Math.floor(Math.random() * CUSTOMER_NAMES.length)];
          addNotification(`${customerName} prepaid $${scenario.amount} on Pump ${scenario.pump}`, 'info');
          
          // Use functional update to check current pump state
          setPumps(prev => {
            const targetPump = prev.find(p => p.id === scenario.pump);
            if (targetPump && targetPump.status === PUMP_STATUS.IDLE) {
              return prev.map(p => 
                p.id === scenario.pump 
                  ? { ...p, status: PUMP_STATUS.AUTHORIZED, presetAmount: scenario.amount, selectedGrade: scenario.grade, customerName }
                  : p
              );
            }
            return prev;
          });
          
          // Start pumping after delay
          setTimeout(() => {
            setPumps(prev => prev.map(p => 
              p.id === scenario.pump && p.status === PUMP_STATUS.AUTHORIZED
                ? { ...p, status: PUMP_STATUS.PUMPING }
                : p
            ));
          }, 1500 / demoSpeed);
        } else if (scenario.type === 'store_sale') {
          const items = scenario.items.map(id => STORE_PRODUCTS.find(p => p.id === id)).filter(Boolean);
          const total = items.reduce((sum, item) => sum + item.price, 0);
          addNotification(`C-Store sale: ${items.length} items - $${total.toFixed(2)}`, 'success');
          setTodayStats(prev => ({
            ...prev,
            totalSales: prev.totalSales + total,
            storesSales: prev.storesSales + total,
            transactions: prev.transactions + 1,
          }));
        }
      }, scenario.delay / demoSpeed);
    });

    // Loop demo - reset completed pumps and trigger new cycle
    const loopTimeout = setTimeout(() => {
      setPumps(prev => prev.map(p => 
        p.status === PUMP_STATUS.COMPLETE 
          ? { ...p, status: PUMP_STATUS.IDLE, currentAmount: 0, currentGallons: 0, presetAmount: null, selectedGrade: null, customerName: null }
          : p
      ));
      // Trigger next demo cycle
      setDemoCycle(c => c + 1);
    }, 16000 / demoSpeed);

    return () => {
      timeouts.forEach(clearTimeout);
      clearTimeout(loopTimeout);
    };
  }, [demoRunning, demoSpeed, demoCycle, addNotification]);

  // Handle pump selection
  const handleSelectPump = (pump) => {
    setSelectedPump(pump);
    if (pump.status === PUMP_STATUS.IDLE) {
      setShowPrepayDialog(true);
    } else if (pump.status === PUMP_STATUS.COMPLETE) {
      // Collect payment
      const fuelItem = {
        id: `fuel-${pump.id}-${Date.now()}`,
        name: `Pump ${pump.id} - ${FUEL_GRADES.find(g => g.id === pump.selectedGrade)?.name}`,
        price: pump.currentAmount,
        quantity: 1,
        isFuel: true,
        gallons: pump.currentGallons,
      };
      setCart(prev => [...prev, fuelItem]);
      setTodayStats(prev => ({
        ...prev,
        totalSales: prev.totalSales + pump.currentAmount,
        fuelSales: prev.fuelSales + pump.currentAmount,
        gallonsSold: prev.gallonsSold + pump.currentGallons,
        transactions: prev.transactions + 1,
      }));
      
      // Reset pump
      setPumps(prev => prev.map(p => 
        p.id === pump.id 
          ? { ...p, status: PUMP_STATUS.IDLE, currentAmount: 0, currentGallons: 0, presetAmount: null, selectedGrade: null }
          : p
      ));
      addNotification(`Collected $${pump.currentAmount.toFixed(2)} from Pump ${pump.id}`, 'success');
    }
  };

  // Handle prepay
  const handlePrepay = (amount, grade) => {
    if (!selectedPump) return;
    const customerName = CUSTOMER_NAMES[Math.floor(Math.random() * CUSTOMER_NAMES.length)];
    
    setPumps(prev => prev.map(p => 
      p.id === selectedPump.id 
        ? { ...p, status: PUMP_STATUS.AUTHORIZED, presetAmount: amount, selectedGrade: grade, customerName }
        : p
    ));
    
    addNotification(`Pump ${selectedPump.id} authorized for $${amount}`, 'info');
    setShowPrepayDialog(false);
    
    // Auto start pumping
    setTimeout(() => {
      setPumps(prev => prev.map(p => 
        p.id === selectedPump.id && p.status === PUMP_STATUS.AUTHORIZED
          ? { ...p, status: PUMP_STATUS.PUMPING }
          : p
      ));
    }, 2000);
  };

  // Handle store product
  const handleAddProduct = (product) => {
    if (product.ageRestricted) {
      setPendingProduct(product);
      setShowAgeVerify(true);
      return;
    }
    addToCart(product);
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id && !item.isFuel);
      if (existing) {
        return prev.map(item => 
          item.id === product.id && !item.isFuel 
            ? { ...item, quantity: item.quantity + 1 } 
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    addNotification(`Added ${product.name} to cart`, 'info');
  };

  // Cart totals
  const cartSubtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartTax = cartSubtotal * 0.0825;
  const cartTotal = cartSubtotal + cartTax;

  // Complete transaction
  const handleCheckout = (method) => {
    if (cart.length === 0) return;
    addNotification(`Payment received: $${cartTotal.toFixed(2)} via ${method}`, 'success');
    setCart([]);
  };

  // Reset demo
  const resetDemo = () => {
    setPumps(createInitialPumps());
    setCart([]);
    setTodayStats({
      totalSales: 2847.50,
      gallonsSold: 862,
      transactions: 47,
      storesSales: 534.20,
      fuelSales: 2313.30,
    });
    setNotifications([]);
    addNotification('Demo reset to initial state', 'info');
  };

  // Filter products
  const filteredProducts = STORE_PRODUCTS.filter(p => p.category === selectedCategory);

  return (
    <div className="h-screen bg-slate-900 text-white flex flex-col overflow-hidden relative">
      {/* Demo Mode Banner */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white py-2 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
            <Monitor className="h-4 w-4" />
            <span className="text-sm font-bold">DEMO MODE</span>
          </div>
          <span className="text-sm hidden sm:inline">Interactive Sales Demonstration - FlowFeed Gas Station POS</span>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant={demoRunning ? "secondary" : "default"}
            onClick={() => setDemoRunning(!demoRunning)}
            className={demoRunning ? "bg-white text-purple-600" : "bg-white/20 hover:bg-white/30"}
            data-testid="auto-demo-btn"
          >
            {demoRunning ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
            {demoRunning ? 'Pause Demo' : 'Auto Demo'}
          </Button>
          <Button 
            size="sm" 
            variant="ghost"
            onClick={() => setDemoSpeed(demoSpeed >= 3 ? 1 : demoSpeed + 1)}
            className="bg-white/20 hover:bg-white/30"
          >
            <Zap className="h-4 w-4 mr-1" />
            {demoSpeed}x Speed
          </Button>
          <Button 
            size="sm" 
            variant="ghost"
            onClick={resetDemo}
            className="bg-white/20 hover:bg-white/30"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <div className="h-6 w-px bg-white/30 mx-1" />
          <Button 
            size="sm" 
            onClick={() => navigate('/login')}
            className="bg-white text-purple-600 hover:bg-white/90"
            data-testid="login-to-use-btn"
          >
            <LogIn className="h-4 w-4 mr-1" />
            Login to Use
          </Button>
        </div>
      </div>

      {/* Notifications */}
      <div className="fixed top-16 right-4 z-50 space-y-2">
        <AnimatePresence>
          {notifications.map((n) => (
            <motion.div
              key={n.id}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className={`px-4 py-2 rounded-lg shadow-lg ${
                n.type === 'success' ? 'bg-emerald-500' : 
                n.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
              }`}
            >
              <p className="text-sm font-medium">{n.message}</p>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Main Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800/50 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
            <Fuel className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold">FlowFeed Gas Station</h1>
            <p className="text-xs text-slate-400">8 Pumps • C-Store Enabled</p>
          </div>
        </div>

        {/* Live Stats */}
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-slate-400">Today's Sales</p>
            <p className="text-lg font-bold text-emerald-400">${todayStats.totalSales.toFixed(2)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">Gallons</p>
            <p className="text-lg font-bold text-blue-400">{Math.round(todayStats.gallonsSold)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">Transactions</p>
            <p className="text-lg font-bold text-purple-400">{todayStats.transactions}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Fuel Pumps */}
        <div className="w-1/2 border-r border-slate-700 flex flex-col">
          {/* Pump Stats Bar */}
          <div className="grid grid-cols-4 gap-2 p-3 border-b border-slate-700">
            <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-emerald-400">
                {pumps.filter(p => p.status === PUMP_STATUS.PUMPING).length}
              </p>
              <p className="text-[10px] text-slate-400">Pumping</p>
            </div>
            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-yellow-400">
                {pumps.filter(p => p.status === PUMP_STATUS.AUTHORIZED).length}
              </p>
              <p className="text-[10px] text-slate-400">Authorized</p>
            </div>
            <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-blue-400">
                {pumps.filter(p => p.status === PUMP_STATUS.COMPLETE).length}
              </p>
              <p className="text-[10px] text-slate-400">Complete</p>
            </div>
            <div className="bg-slate-500/20 border border-slate-500/30 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-slate-400">
                {pumps.filter(p => p.status === PUMP_STATUS.IDLE).length}
              </p>
              <p className="text-[10px] text-slate-400">Available</p>
            </div>
          </div>

          {/* Pump Grid */}
          <div className="flex-1 p-4 overflow-auto">
            <div className="grid grid-cols-4 gap-3">
              {pumps.map((pump) => (
                <motion.div
                  key={pump.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelectPump(pump)}
                  className={`relative p-3 rounded-xl border-2 cursor-pointer transition-all min-h-[140px]
                    ${pump.status === PUMP_STATUS.IDLE ? 'border-slate-600 bg-slate-800/50 hover:border-slate-500' : ''}
                    ${pump.status === PUMP_STATUS.AUTHORIZED ? 'border-yellow-500 bg-yellow-500/10' : ''}
                    ${pump.status === PUMP_STATUS.PUMPING ? 'border-emerald-500 bg-emerald-500/10' : ''}
                    ${pump.status === PUMP_STATUS.COMPLETE ? 'border-blue-500 bg-blue-500/10' : ''}
                  `}
                >
                  {/* Pump Number */}
                  <div className="absolute -top-2 -left-2 h-7 w-7 rounded-full bg-slate-700 border-2 border-slate-600 flex items-center justify-center">
                    <span className="text-sm font-bold">{pump.id}</span>
                  </div>

                  {/* Grade Badge */}
                  {pump.selectedGrade && (
                    <div className="absolute -top-2 -right-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold text-white ${FUEL_GRADES.find(g => g.id === pump.selectedGrade)?.color}`}>
                        {FUEL_GRADES.find(g => g.id === pump.selectedGrade)?.name}
                      </span>
                    </div>
                  )}

                  {/* Status Animation */}
                  {pump.status === PUMP_STATUS.PUMPING && (
                    <motion.div 
                      className="absolute top-2 right-2"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                    >
                      <Droplets className="h-4 w-4 text-emerald-400" />
                    </motion.div>
                  )}

                  {/* Amount Display */}
                  <div className="text-center mt-4">
                    <p className="text-2xl font-bold font-mono">${pump.currentAmount.toFixed(2)}</p>
                    <p className="text-xs text-slate-400">{pump.currentGallons.toFixed(3)} gal</p>
                    
                    {/* Progress Bar */}
                    {pump.presetAmount && (
                      <div className="mt-2">
                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                          <motion.div 
                            className="bg-emerald-500 h-1.5 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${(pump.currentAmount / pump.presetAmount) * 100}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">
                          {pump.customerName} • ${pump.presetAmount}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  {pump.status === PUMP_STATUS.COMPLETE && (
                    <Button size="sm" className="w-full mt-2 bg-blue-600 hover:bg-blue-700 h-8 text-xs">
                      <DollarSign className="h-3 w-3 mr-1" /> Collect
                    </Button>
                  )}
                  {pump.status === PUMP_STATUS.IDLE && (
                    <p className="text-center text-[10px] text-slate-500 mt-4">Tap to Prepay</p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Fuel Prices & Tank Levels */}
          <div className="p-3 border-t border-slate-700 bg-slate-800/30">
            <div className="grid grid-cols-4 gap-2">
              {FUEL_GRADES.map((grade) => (
                <div key={grade.id} className="text-center">
                  <div className={`h-2 w-full rounded-full bg-slate-700 mb-1`}>
                    <div 
                      className={`h-2 rounded-full ${grade.color}`}
                      style={{ width: `${tankLevels[grade.id] || 50}%` }}
                    />
                  </div>
                  <p className="text-xs font-bold">${grade.price.toFixed(3)}</p>
                  <p className="text-[10px] text-slate-400">{grade.name} • {tankLevels[grade.id]}%</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Convenience Store */}
        <div className="w-1/4 flex flex-col border-r border-slate-700">
          <div className="p-3 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <Store className="h-5 w-5 text-emerald-400" />
              <h2 className="font-bold">C-Store</h2>
            </div>
          </div>

          {/* Categories */}
          <div className="flex gap-1 p-2 border-b border-slate-700 overflow-x-auto">
            {STORE_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs whitespace-nowrap transition-colors
                    ${selectedCategory === cat.id ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400'}`}
                >
                  <Icon className="h-3 w-3" />
                  {cat.name}
                </button>
              );
            })}
          </div>

          {/* Products */}
          <div className="flex-1 p-2 overflow-auto">
            <div className="grid grid-cols-2 gap-2">
              {filteredProducts.map((product) => (
                <motion.button
                  key={product.id}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleAddProduct(product)}
                  className="bg-slate-800 hover:bg-slate-700 rounded-xl p-2 text-left relative"
                >
                  {product.ageRestricted && (
                    <Badge className="absolute -top-1 -right-1 bg-yellow-500 text-[8px] px-1">21+</Badge>
                  )}
                  <div className="text-xl mb-1">{product.icon}</div>
                  <p className="text-xs font-medium truncate">{product.name}</p>
                  <p className="text-xs font-bold text-emerald-400">${product.price.toFixed(2)}</p>
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Cart */}
        <div className="w-1/4 flex flex-col bg-slate-800/30">
          <div className="p-3 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-emerald-400" />
                <h2 className="font-bold">Cart</h2>
              </div>
              <Badge variant="secondary">{cart.length}</Badge>
            </div>
          </div>

          {/* Cart Items */}
          <div className="flex-1 p-2 overflow-auto space-y-2">
            {cart.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Empty cart</p>
              </div>
            ) : (
              cart.map((item, idx) => (
                <div key={`${item.id}-${idx}`} className="bg-slate-800 rounded-lg p-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium">{item.name}</p>
                      {item.isFuel && <p className="text-[10px] text-slate-400">{item.gallons?.toFixed(3)} gal</p>}
                    </div>
                    <p className="text-sm font-bold text-emerald-400">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Totals */}
          <div className="p-3 border-t border-slate-700 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Subtotal</span>
              <span>${cartSubtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Tax (8.25%)</span>
              <span>${cartTax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-600">
              <span>Total</span>
              <span className="text-emerald-400">${cartTotal.toFixed(2)}</span>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleCheckout('Cash')}
                disabled={cart.length === 0}
              >
                <Banknote className="h-4 w-4 mr-1" /> Cash
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => handleCheckout('Card')}
                disabled={cart.length === 0}
              >
                <CreditCard className="h-4 w-4 mr-1" /> Card
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Prepay Dialog */}
      <Dialog open={showPrepayDialog} onOpenChange={setShowPrepayDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fuel className="h-5 w-5 text-emerald-400" />
              Prepay - Pump {selectedPump?.id}
            </DialogTitle>
          </DialogHeader>
          <PrepayForm onSubmit={handlePrepay} onCancel={() => setShowPrepayDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* Age Verification Dialog */}
      <Dialog open={showAgeVerify} onOpenChange={setShowAgeVerify}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-400">
              <Shield className="h-5 w-5" />
              Age Verification Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-300">
              <strong>{pendingProduct?.name}</strong> requires customer to be 21+ years old.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={() => setShowAgeVerify(false)}>Cancel</Button>
              <Button 
                className="bg-emerald-600"
                onClick={() => {
                  addToCart(pendingProduct);
                  setShowAgeVerify(false);
                  setPendingProduct(null);
                }}
              >
                ID Verified
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Prepay Form Component
const PrepayForm = ({ onSubmit, onCancel }) => {
  const [amount, setAmount] = useState('');
  const [grade, setGrade] = useState('regular');
  const quickAmounts = [10, 20, 25, 30, 40, 50];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        {quickAmounts.map((amt) => (
          <Button
            key={amt}
            size="sm"
            variant={amount === amt.toString() ? 'default' : 'outline'}
            className={amount === amt.toString() ? 'bg-emerald-600' : 'border-slate-600'}
            onClick={() => setAmount(amt.toString())}
          >
            ${amt}
          </Button>
        ))}
      </div>
      <div className="relative">
        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Custom amount"
          className="pl-9 bg-slate-800 border-slate-600"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {FUEL_GRADES.map((g) => (
          <Button
            key={g.id}
            size="sm"
            variant="outline"
            className={`text-xs ${grade === g.id ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-600'}`}
            onClick={() => setGrade(g.id)}
          >
            <span className={`h-2 w-2 rounded-full ${g.color} mr-1`}></span>
            {g.name}
          </Button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button 
          className="bg-emerald-600"
          onClick={() => onSubmit(parseFloat(amount), grade)}
          disabled={!amount || parseFloat(amount) <= 0}
        >
          Authorize
        </Button>
      </div>
    </div>
  );
};

export default GasStationDemo;
